// Private native-launch fragments are captured before routing or session lookup.
import { req } from "../api.ts";
import { esc } from "../dom.ts";
import { navigate, type Params } from "../router.ts";
import { playPage } from "../screens/play.ts";
import { Chessground } from "chessground";
import { onDisconnect } from "../ui/lifecycle.ts";
import { commandId, ensurePlayer, readPlayer, sessionRecovery, type Result, type Slot } from "./client.ts";
const key = (id:string) => `chessme.owner-import.v1:${id}`;
interface Record { owner_ticket?:string; sender_verifier?:string; request?: {actor_id:string;command_id:string;owner_ticket:string;sender_verifier:string;browser_proof:string}; slot_id?:string }
let captureFailure = "";
export function captureOwnerFragment(): void {
  const match = /^\/play\/import\/([A-Za-z0-9_-]{24})\/?$/.exec(location.pathname);
  if (!match || !location.hash) return;
  const fragment = location.hash.slice(1);
  // Remove even invalid fragments. Proofs never become navigable share links.
  history.replaceState(history.state,"",location.pathname);
  try {
    const parts = new URLSearchParams(fragment);
    const ticket = parts.get("ticket"), proof = parts.get("proof");
    if ([...parts.keys()].length !== 2 || !ticket || !proof || !/^[A-Za-z0-9_-]{43}$/.test(ticket) || !/^[A-Za-z0-9_-]{43}$/.test(proof)) throw new Error("This owner link is invalid. Open the position again from ChessME.");
    const old = sessionStorage.getItem(key(match[1]));
    const record:Record = old ? JSON.parse(old) : {};
    if (!record.slot_id && (record.owner_ticket !== ticket || record.sender_verifier !== proof)) {
      sessionStorage.setItem(key(match[1]),JSON.stringify({owner_ticket:ticket,sender_verifier:proof}));
    }
  } catch { captureFailure = "Could not save this owner link. Allow browser storage and open the position again from ChessME."; }
}
function browserProof(): string {
  return btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32)))).replaceAll("+","-").replaceAll("/","_").replaceAll("=","");
}
export async function ownerImportScreen(params:Params): Promise<HTMLElement> {
  const id=params.id;
  let record:Record;
  try {
    if(captureFailure) throw new Error(captureFailure);
    const saved=sessionStorage.getItem(key(id));
    if(!saved) throw new Error("Open this position from ChessME in this browser. A friend's invitation is a separate link.");
    record=JSON.parse(saved);
    if(record.slot_id) { navigate(`/play/slot/${record.slot_id}`); return playPage("<p>Opening your invitation…</p>"); }
    if(!record.owner_ticket||!record.sender_verifier) throw new Error("Open this position again from ChessME.");
  } catch(error) { return playPage(`<h1>Open your position</h1><p>${esc(error instanceof Error?error.message:"Browser storage is unavailable.")}</p><a class="btn" data-link href="/">Play a fresh game</a>`); }
  let preview:{state:string;slot_id?:string;setup?:Slot["setup"];expires_at?:number};
  try { preview=await req("POST",`/api/play/imports/${id}/preview`,{owner_ticket:record.owner_ticket,sender_verifier:record.sender_verifier}); }
  catch(error) { return playPage(`<h1>Open the position again from ChessME</h1><p>${esc(error instanceof Error?error.message:"This owner link is unavailable.")}</p><a class="btn" data-link href="/">Play a fresh game</a>`); }
  function remember(slot:string):void {
    sessionStorage.setItem(key(id),JSON.stringify({slot_id:slot}));
    sessionStorage.setItem("chessme.last-slot.v1",slot);
    navigate(`/play/slot/${slot}`);
  }
  if(preview.state==="bound"&&preview.slot_id) {
    const current=await readPlayer();
    if(record.request&&current?.id===record.request.actor_id) {
      const receipt=await req<Result>("POST",`/api/play/imports/${id}/exchange`,record.request);
      if(receipt.command_id!==record.request.command_id||!receipt.slot) throw new Error("Could not check the original import request.");
      remember(receipt.slot.id);
    } else remember(preview.slot_id);
    return playPage("<p>Opening the existing invitation…</p>");
  }
  if (!preview.setup?.transfer?.snapshot.final_fen) return playPage('<h1>Position unavailable</h1><p>Open the original position again from ChessME.</p><a data-link href="/">Play a fresh game</a>');
  const color=preview.setup?.transfer?.source.initiator_color??"white";
  const root=playPage(`<h1>Continue this position</h1><p>You keep ${color}. Standard chess · no clock · unrated.</p>
    <div class="position-preview cg-board-wrap"></div><p>Continue together when your friend accepts. You can keep playing in Messages until the live board starts.</p>
    <p class="fine">A new Messages move ends the pending offer.</p><button class="btn primary" data-continue>Continue in this browser</button><p class="msg" role="status" aria-live="polite"></p>`);
  root.classList.add("live-offer");
  requestAnimationFrame(()=> {
    if(!root.isConnected)return;
    const board=Chessground(root.querySelector<HTMLElement>(".position-preview")!,{fen:preview.setup?.transfer?.snapshot.final_fen,orientation:color,viewOnly:true});
    onDisconnect(root,()=>board.destroy());
  });
  const button=root.querySelector<HTMLButtonElement>("[data-continue]")!;
  button.onclick=async()=> {
    button.disabled=true;
    try {
      const player=await ensurePlayer();
      if(record.request&&record.request.actor_id!==player.id) throw new Error("Your browser session changed. Return to the browser that started this request.");
      record.request??={actor_id:player.id,command_id:commandId(),owner_ticket:record.owner_ticket!,sender_verifier:record.sender_verifier!,browser_proof:browserProof()};
      sessionStorage.setItem(key(id),JSON.stringify(record));
      const result=await req<Result>("POST",`/api/play/imports/${id}/exchange`,record.request);
      if(result.command_id!==record.request.command_id||!result.slot) throw new Error("Could not verify the imported invitation. Retry the same request.");
      remember(result.slot.id);
    } catch(error) { root.querySelector(".msg")!.textContent=error instanceof Error?error.message:"Please retry.";button.disabled=false;sessionRecovery(root,error); }
  };
  return root;
}
